import livestreamActions from '../actions/LivestreamActions';

export const defaultState = {
    audioDeviceId: null,
    audioDevices: [],
    hardwareAccessStatus: livestreamActions.hardwareAccessStatus.NOT_REQUESTED,
    hasConfirmedHardwareSettings: false,
    isAudioStreamingStarted: false,
    isRoomScanConfirmed: false,
    isWebcamStreamingStarted: false,
    liveSessionId: null,
    registeredCalls: {},
    /**
     * When speakerDevices is an empty Array, null means "no chosen device".
     * When speakerDevices is filled, null means "the default device".
     * Otherwise, it must contain the device ID of any device THAT IS NOT the
     * default device.
     */
    speakerDeviceId: null,
    /**
     * In this context, speaker device means an aufio output device such as
     * a laptop's speaker, headphones, etc.
     */
    speakerDevices: [],
    videoDevices: [],
    webcamDeviceId: null,
};

function setLivestreamAccessGranted(state, devices) {
    const parsedVideoDevices = [];
    const parsedAudioDevices = [];
    const parsedSpeakerDevices = [];
    devices.forEach((device) => {
        const { deviceId, kind, label } = device;

        if (kind === 'videoinput') {
            parsedVideoDevices.push({
                deviceId,
                label,
            });
        } else if (kind === 'audioinput') {
            parsedAudioDevices.push({
                deviceId,
                label,
            });
        } else if (kind === 'audiooutput') {
            parsedSpeakerDevices.push({
                deviceId,
                label,
            });
        }
    });

    /**
     * As this function is called even when speakerDeviceId isn't modified,
     * leave the speakerDeviceId as is.
     */
    return Object.assign({}, state, {
        hardwareAccessStatus: livestreamActions.hardwareAccessStatus.GRANTED,
        videoDevices: parsedVideoDevices,
        audioDevices: parsedAudioDevices,
        speakerDevices: parsedSpeakerDevices,
        webcamDeviceId: parsedVideoDevices.length ? parsedVideoDevices[0].deviceId : null,
        audioDeviceId: parsedAudioDevices.length ? parsedAudioDevices[0].deviceId : null,
        speakerDeviceId: parsedSpeakerDevices?.length ? parsedSpeakerDevices[0].deviceId : null,
    });
}

function setOfferer(state, messagerId, proctoringMode, proctoringModeSetTime, liveSessionTimestamp) {
    const existingCall = state.registeredCalls[messagerId];
    if (existingCall && existingCall.liveSessionTimestamp > liveSessionTimestamp) {
        return state;
    }

    const call = {
        [messagerId]: {
            proctoringMode,
            proctoringModeSetTime,
            liveSessionTimestamp,
            connectionStatus: livestreamActions.liveSessionConnectionStatus.CONNECTED,
        },
    };
    return Object.assign({}, state, {
        registeredCalls: { ...state.registeredCalls, ...call },
    });
}

function setProctoringMode(state, messagerId, proctoringMode, proctoringModeSetTime) {
    const registeredCall = state.registeredCalls[messagerId];
    if (!registeredCall) {
        return state;
    }

    const call = {
        [messagerId]: {
            ...registeredCall,
            proctoringMode,
            proctoringModeSetTime,
        },
    };
    return Object.assign({}, state, {
        registeredCalls: { ...state.registeredCalls, ...call },
    });
}

function removeOfferer(state, messagerId) {
    const newState = Object.assign({}, state, {
        registeredCalls: { ...state.registeredCalls },
    });
    delete newState.registeredCalls[messagerId];
    return newState;
}

function deemSessionAsFailed(state, messagerId) {
    const call = {
        [messagerId]: {
            ...state.registeredCalls[messagerId],
            problem: livestreamActions.sessionProblem.SETUP_FAILED,
        },
    };
    return Object.assign({}, state, {
        registeredCalls: { ...state.registeredCalls, ...call },
    });
}

function setConnectionStatus(state, messagerId, connectionStatus, liveSessionTimestamp) {
    const existingCall = state.registeredCalls[messagerId];
    if (existingCall && existingCall.liveSessionTimestamp > liveSessionTimestamp) {
        return state;
    }

    const call = {
        [messagerId]: {
            ...state.registeredCalls[messagerId],
            connectionStatus,
            liveSessionTimestamp,
        },
    };
    return Object.assign({}, state, {
        registeredCalls: { ...state.registeredCalls, ...call },
    });
}

function setSpeakerDeviceId(state, actionDeviceId) {
    const { speakerDevices } = state;
    let speakerDeviceId = actionDeviceId;

    /**
     * In W3C specification of enumerateDevices, it is indicated that the first
     * device represents the default device.
     *
     * However, setSinkId default value is an empty string and not the deviceId
     * of the first device, probably to not expose a deviceId when
     * enumerateDevices is not called.
     *
     * To reproduce that behavior and not use setSinkId when the default device
     * is chosen, speakerDeviceId MUST be null when the first speaker device is
     * selected. Hence why here I set it explicitly to null.
     *
     * audioSinkService will correctly map null or undefined to an empty string.
     */
    if (
        speakerDevices?.[0]?.deviceId === actionDeviceId
    ) {
        speakerDeviceId = null;
    }

    return {
        ...state,
        speakerDeviceId,
    };
}

export default function (state = defaultState, action) {
    switch (action.type) {
    case livestreamActions.types.LIVESTREAM_HARDWARE_ACCESS_GRANTED:
        return setLivestreamAccessGranted(state, action.devices);

    case livestreamActions.types.LIVESTREAM_HARDWARE_ACCESS_REJECTED:
        return Object.assign({}, defaultState, {
            hardwareAccessStatus: livestreamActions.hardwareAccessStatus.REJECTED,
        });

    case livestreamActions.types.LIVESTREAM_HARDWARE_ACCESS_CLOSED:
        return Object.assign({}, state, {
            hardwareAccessStatus: livestreamActions.hardwareAccessStatus.CLOSED,
        });

    case livestreamActions.types.LIVESTREAM_WEBCAM_STARTED:
        return Object.assign({}, state, {
            isWebcamStreamingStarted: true,
        });

    case livestreamActions.types.LIVESTREAM_AUDIO_STARTED:
        return Object.assign({}, state, {
            isAudioStreamingStarted: true,
        });

    case livestreamActions.types.LIVESTREAM_CONFIRM_HARDWARE_SETTINGS:
        return Object.assign({}, state, {
            hasConfirmedHardwareSettings: true,
        });
    case livestreamActions.types.LIVESTREAM_ABORT_HARDWARE_SETTINGS:
        return Object.assign({}, state, {
            hasConfirmedHardwareSettings: false,
        });

    case livestreamActions.types.LIVESTREAM_SWITCH_DEVICES:
        return Object.assign({}, state, {
            webcamDeviceId: action.webcamDeviceId,
            audioDeviceId: action.audioDeviceId,
        });

    case livestreamActions.types.LIVESTREAM_SWITCH_SPEAKER_DEVICE:
        return setSpeakerDeviceId(state, action.speakerDeviceId);

    case livestreamActions.types.LIVESTREAM_SET_ROOM_SCAN_CONFIRMATION:
        return Object.assign({}, state, {
            isRoomScanConfirmed: action.isConfirmed,
        });

    case livestreamActions.types.LIVESTREAM_SET_LIVESESSION:
        return Object.assign({}, state, {
            liveSessionId: action.liveSessionId,
        });

    case livestreamActions.types.LIVESTREAM_SET_OFFERER:
        return setOfferer(state, action.messagerId, action.proctoringMode, action.proctoringModeSetTime, action.liveSessionTimestamp);

    case livestreamActions.types.LIVESTREAM_SET_PROCTORING_MODE:
        return setProctoringMode(state, action.messagerId, action.proctoringMode, action.proctoringModeSetTime);

    case livestreamActions.types.LIVESTREAM_REMOVE_OFFERER:
        return removeOfferer(state, action.messagerId);

    case livestreamActions.types.LIVESTREAM_SETUP_FAILED:
        return deemSessionAsFailed(state, action.messagerId);

    case livestreamActions.types.LIVESTREAM_SET_OFFERER_CONNECTION_STATUS:
        return setConnectionStatus(
            state,
            action.messagerId,
            action.connectionStatus,
            action.liveSessionTimestamp,
        );

    default:
        return state;
    }
}
